/* ============================================================
   Dropps-clone — minimal JS
   Cart drawer, accordion, tabs, PDP toggle.
   No frameworks. Vanilla.
   ============================================================ */

(function () {
  'use strict';

  /* ------- Welcome popup -------
     Fires on first visit (or after 7-day cooldown), captures email,
     and shows a discount code on success. State persisted via
     localStorage. */
  (function welcomePopup() {
    const popup = document.querySelector('[data-promo-popup]');
    if (!popup) return;

    const STORAGE_KEY = 'rena_promo_popup_dismissed_at';
    const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;   // 7 days

    // Skip if user dismissed within cooldown window.
    try {
      const last = parseInt(localStorage.getItem(STORAGE_KEY), 10);
      if (last && (Date.now() - last) < COOLDOWN_MS) return;
    } catch (e) { /* localStorage blocked — fall through and show once per session */ }

    // Skip if the URL contains a Shopify newsletter-signup confirmation
    // (e.g. ?customer_posted=true) — don't re-show right after they
    // submitted from another form on the site.
    if (location.search.indexOf('customer_posted=true') !== -1) {
      try { localStorage.setItem(STORAGE_KEY, Date.now()); } catch (e) {}
      // Show the success view directly.
      showPopup();
      switchView('success');
      return;
    }

    const delay = parseInt(popup.dataset.promoDelay, 10) || 5;
    const showTimer = setTimeout(() => showPopup(), delay * 1000);

    function showPopup() {
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function hidePopup() {
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      try { localStorage.setItem(STORAGE_KEY, Date.now()); } catch (e) {}
      clearTimeout(showTimer);
    }
    function switchView(name) {
      popup.querySelectorAll('[data-promo-view]').forEach((v) => {
        v.hidden = v.dataset.promoView !== name;
      });
    }

    // Close handlers (X button, backdrop, anything tagged data-promo-close).
    popup.addEventListener('click', (e) => {
      if (e.target.closest('[data-promo-close]')) hidePopup();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.getAttribute('aria-hidden') === 'false') hidePopup();
    });

    // Intercept form submit — POST to /contact and show success state
    // without navigating away from the page.
    const form = popup.querySelector('form');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;
        try {
          const fd = new FormData(form);
          fd.set('form_type', 'customer');
          fd.set('utf8', '✓');
          await fetch(form.action || '/contact#newsletter', {
            method: 'POST',
            body: fd,
            headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
          });
        } catch (err) { /* fail silently — show success either way */ }
        switchView('success');
        try { localStorage.setItem(STORAGE_KEY, Date.now()); } catch (e) {}
      });
    }
  })();

  /* ------- Language switcher loading state -------
     Locale switching is a full page reload (POST to /localization →
     302 → GET new page) which takes 1-3s. Mark the form's button
     disabled + add .is-switching class on submit so the user gets
     immediate feedback that their click registered. */
  document.querySelectorAll('.site-header__lang-form, .mobile-menu__locale-form').forEach((form) => {
    form.addEventListener('submit', () => {
      const btn = form.querySelector('button[type="submit"]');
      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-switching');
      }
    });
  });

  /* ------- Cart drawer ------- */
  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-drawer-overlay]');
  const cartLink = document.querySelectorAll('[data-cart-toggle]');
  const cartClose = document.querySelectorAll('[data-cart-close]');

  function openDrawer() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* Header nav dropdown — toggle a body class so the full-page scrim shows.
     Same treatment for the sign-in account dropdown (narrow, but still
     emerges from the header and dims the page behind it). */
  document.querySelectorAll('.nav-item, .header-action').forEach((item) => {
    item.addEventListener('mouseenter', () => document.body.classList.add('is-nav-open'));
    item.addEventListener('mouseleave', () => document.body.classList.remove('is-nav-open'));
    item.addEventListener('focusin',   () => document.body.classList.add('is-nav-open'));
    item.addEventListener('focusout',  () => document.body.classList.remove('is-nav-open'));
  });

  /* ------- Mobile drawer ------- */
  (function mobileMenu() {
    const drawer = document.querySelector('[data-mobile-menu]');
    const scrim  = document.querySelector('[data-mobile-menu-scrim]');
    const openBtn  = document.querySelector('[data-mobile-menu-open]');
    const closeBtn = document.querySelector('[data-mobile-menu-close]');
    if (!drawer || !openBtn) return;

    const setOpen = (open) => {
      document.body.classList.toggle('is-mobile-menu-open', open);
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      // Lock body scroll behind the drawer
      document.body.style.overflow = open ? 'hidden' : '';
    };

    openBtn.addEventListener('click', () => setOpen(true));
    closeBtn && closeBtn.addEventListener('click', () => setOpen(false));
    scrim    && scrim.addEventListener('click',    () => setOpen(false));

    // ESC closes
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.body.classList.contains('is-mobile-menu-open')) {
        setOpen(false);
      }
    });

    // Resize past the breakpoint → force-close so state can't get stuck
    let lastWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      const w = window.innerWidth;
      if (lastWidth <= 900 && w > 900) setOpen(false);
      lastWidth = w;
    });
  })();

  /* Expose the sticky header's bottom edge as a CSS variable so the
     full-width nav dropdown + scrim can anchor below it regardless of
     announcement bar height. */
  const stickyTop = document.querySelector('.sticky-top');
  if (stickyTop) {
    const setHeaderBottom = () => {
      const h = Math.round(stickyTop.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-bottom', h + 'px');
    };
    setHeaderBottom();
    if ('ResizeObserver' in window) new ResizeObserver(setHeaderBottom).observe(stickyTop);
    window.addEventListener('resize', setHeaderBottom);

    /* Mobile: collapse the announcement bar + category chip row together on
       scroll-down, reveal both on scroll-up. The main header row stays
       pinned regardless. */
    const chipRow         = document.querySelector('.site-header__chips');
    const announcementBar = document.querySelector('.announcement-bar');
    const collapsibles    = [chipRow, announcementBar].filter(Boolean);
    if (collapsibles.length) {
      let lastY            = window.scrollY;
      let hideTicking      = false;
      let lastToggleAt     = 0;
      let currentHidden    = false;
      const HIDE_AFTER_PX  = 80;     // don't hide near the top of the page
      const SCROLL_DELTA   = 18;     // minimum decisive scroll to flip state
      const COOLDOWN_MS    = 340;    // wait this long after a toggle before next
      const setHidden = (hidden) => {
        if (hidden === currentHidden) return;          // no-op when state matches
        currentHidden = hidden;
        lastToggleAt  = performance.now();
        collapsibles.forEach((el) => el.classList.toggle('is-hidden', hidden));
      };
      const updateHide = () => {
        const y = window.scrollY;
        const isMobile = window.innerWidth <= 900;
        const now = performance.now();

        if (!isMobile) {
          setHidden(false);
        } else if (now - lastToggleAt < COOLDOWN_MS) {
          // Animation cooldown — ignore scroll-driven toggles until it ends.
        } else if (y > HIDE_AFTER_PX && y - lastY > SCROLL_DELTA) {
          setHidden(true);                              // scrolling down → collapse
        } else if (lastY - y > SCROLL_DELTA || y <= HIDE_AFTER_PX) {
          setHidden(false);                             // scrolling up or near top → expand
        }

        // Only update lastY when the user has committed to a clear direction —
        // keeps small sub-threshold scroll noise from drifting the reference.
        if (Math.abs(y - lastY) > SCROLL_DELTA) lastY = y;
        hideTicking = false;
      };
      window.addEventListener('scroll', () => {
        if (!hideTicking) {
          window.requestAnimationFrame(updateHide);
          hideTicking = true;
        }
      }, { passive: true });
    }
  }

  /* AJAX add-to-cart — catch clicks on any ATC button.
     POSTs to /cart/add.js so we stay in-page and open the drawer. */
  const openCartDrawer = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-drawer-overlay]');
    if (drawer) drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pdp-atc], .product-card__atc, button[name="add"]');
    if (!btn) return;
    const form = btn.closest('form');
    if (!form) return;
    e.preventDefault();
    btn.disabled = true;

    const fd = new FormData(form);
    // Make sure FormData contains an `id` field
    if (!fd.get('id')) {
      const hidden = form.querySelector('input[name="id"]');
      if (hidden && hidden.value) fd.set('id', hidden.value);
    }

    // Show the drawer's loading spinner SYNCHRONOUSLY on click so the
    // user gets visual confirmation in the same frame their tap lands —
    // not after /cart/add.js resolves 200-500ms later.
    setDrawerLoading(true);

    // Open the drawer in the SAME frame as the click so the user sees
    // instant feedback. Old cart items are visible (faded by loading
    // state); the new line fills in via refreshCartDrawer() once
    // /cart/add.js + the section/page refresh resolve.
    closeQuickAdd();
    openCartDrawer();

    // Fire the add-to-cart request (don't await it here).
    const addPromise = fetch('/cart/add.js', {
      method: 'POST',
      body: fd,
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });

    addPromise
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          console.warn('Add to cart failed', res.status, json);
          console.log('FormData submitted:', Object.fromEntries(fd.entries()));
          const _fallback = window.__renaT?.couldNotAddToCart || 'Could not add to cart.';
          const _statusTmpl = window.__renaT?.statusPrefix || '(status __CODE__)';
          alert((json.description || json.message || _fallback) + ' ' + _statusTmpl.replace('__CODE__', res.status));
          return;
        }
        return refreshCartDrawer();
      })
      .catch((err) => console.warn('Add to cart error', err))
      .finally(() => {
        setDrawerLoading(false);
        btn.disabled = false;
      });
  });

  /* Cart drawer: quantity stepper + AJAX update.
     Old behavior re-fetched the entire page HTML on every qty change to
     replace the drawer's innerHTML — 1-3s round-trip on mobile and felt
     glacial. New behavior posts /cart/change.js (which already returns
     the full updated cart as JSON) and patches the drawer in place. */

  // Money formatter — faithful port of Shopify's canonical formatMoney.
  // theme.liquid normalises comma-decimal format codes to period-decimal ones
  // and exposes the currency code so we can apply the correct precision and
  // divisor for 3-decimal currencies (OMR, KWD, BHD, TND, JOD …).
  const moneyFormatString = window.__renaMoneyFormat || '{{amount}}';
  const _currency = (window.__renaCurrencyCode || 'USD').toUpperCase();
  // Currencies Shopify CAN store with 3 decimal places (baisa, fils …).
  const _threeDpCurrencies = ['KWD','OMR','BHD','TND','JOD','LYD','IQD'];
  // BUT in practice many Shopify stores using these currencies keep the
  // storage at 2-decimal cents and use a money_format hack — appending a
  // literal "0" after the {{amount}} placeholder — to fake the 3rd decimal
  // in display. Detect that case and treat storage as 2-decimal so the
  // JS-rendered prices match Liquid's | money output exactly.
  const _formatHasZeroHack = moneyFormatString.indexOf('}}0') !== -1;
  const _isThreeDp    = _threeDpCurrencies.includes(_currency) && !_formatHasZeroHack;
  const _priceDivisor = _isThreeDp ? 1000 : 100;
  const _pricePrec    = _isThreeDp ? 3 : 2;

  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    if (precision == null) precision = _pricePrec;
    if (thousands == null) thousands = ',';
    if (decimal   == null) decimal   = '.';
    if (isNaN(number) || number == null) return '0';
    number = (number / _priceDivisor).toFixed(precision);
    const parts  = number.split('.');
    const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const decPart = parts[1] ? (decimal + parts[1]) : '';
    return intPart + decPart;
  };
  const formatMoney = (cents) => {
    if (typeof cents === 'string') cents = parseInt(cents.replace('.', ''), 10);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormatString.match(placeholderRegex);
    if (!match) return String(cents);
    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, _pricePrec); break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, _pricePrec, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, _pricePrec, ' ', '.'); break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, _pricePrec, "'", '.'); break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, _pricePrec, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' '); break;
      default:
        value = formatWithDelimiters(cents, _pricePrec);
    }
    return moneyFormatString.replace(placeholderRegex, value);
  };

  // Fix Liquid-rendered prices: the store's money_format may use a comma as
  // the decimal separator (e.g. "2,800 OMR"). Replace comma-decimal with a
  // period — but ONLY when followed by a 3-decimal currency code, so we never
  // accidentally rewrite a thousands separator like "1,234 items".
  // Match "digit,3digits" immediately followed by whitespace and a 3-dp
  // currency code (OMR/KWD/BHD/TND/JOD/LYD/IQD). Also match the reverse
  // order "OMR 1,234" where the code precedes the amount.
  const _3DP_RE = '(?:OMR|KWD|BHD|TND|JOD|LYD|IQD)';
  const _commaDecAfter  = new RegExp('(\\d),(\\d{3})(?=\\s*' + _3DP_RE + ')', 'gi');
  const _commaDecBefore = new RegExp('(' + _3DP_RE + '\\s*\\d+),(\\d{3})(?!\\d)', 'gi');
  // On Arabic pages, also translate the OMR currency code to ر.ع.
  // Detect Arabic from multiple sources for robustness — locale string, html
  // lang attribute, and html dir attribute (RTL).
  const _htmlLang = (document.documentElement.lang || '').toLowerCase();
  const _htmlDir  = (document.documentElement.dir  || '').toLowerCase();
  const _isArabic =
    (window.__renaLocale || '').toLowerCase().indexOf('ar') === 0 ||
    _htmlLang.indexOf('ar') === 0 ||
    _htmlDir === 'rtl';
  const _omrToArabicRe = /OMR/g;
  const fixPrice = (str) => {
    // Preserve null/undefined so downstream truthiness checks (e.g. hiding
    // the compare-at strike when no compare price exists) behave correctly.
    // Without this, String(null) → "null" leaks into the DOM as visible text.
    if (str == null) return str;
    let out = String(str)
      .replace(_commaDecAfter,  '$1.$2')
      .replace(_commaDecBefore, '$1.$2');
    if (_isArabic) out = out.replace(_omrToArabicRe, 'ر.ع.');
    return out;
  };

  // Walk every text node in the doc and fix any Liquid-rendered prices.
  // Skip <script>, <style>, <noscript> — never want to touch their contents.
  const _SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA']);
  const _fixTextNode = (node) => {
    if (!node.nodeValue) return;
    if (node.parentNode && _SKIP_TAGS.has(node.parentNode.nodeName)) return;
    const fixed = fixPrice(node.nodeValue);
    if (fixed !== node.nodeValue) node.nodeValue = fixed;
  };
  const _fixAllPrices = (root) => {
    root = root || document.body;
    if (!root) return;
    if (root.nodeType === Node.TEXT_NODE) { _fixTextNode(root); return; }
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) _fixTextNode(node);
  };
  const _initPriceFix = () => {
    _fixAllPrices();
    // Catch dynamically-added prices (cart drawer refresh, variant swaps,
    // collection pagination, etc.) via a single MutationObserver.
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === Node.ELEMENT_NODE)      _fixAllPrices(n);
          else if (n.nodeType === Node.TEXT_NODE)    _fixTextNode(n);
        });
        if (m.type === 'characterData') _fixTextNode(m.target);
      }
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _initPriceFix);
  } else {
    _initPriceFix();
  }

  // Patch the cart drawer's totals, line prices, and free-shipping bar
  // from the cart JSON returned by /cart/change.js or /cart.js.
  const patchCartFromJson = (cart) => {
    if (!cart) return;

    // Subtotal
    const subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
      subtotalEl.dataset.cartSubtotalCents = cart.total_price;
    }

    // Cart icon badge + filled/empty visual state
    const cartBtn = document.querySelector('[data-cart-toggle]');
    if (cartBtn) {
      let badge = cartBtn.querySelector('.badge');
      if (cart.item_count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'badge';
          cartBtn.appendChild(badge);
        }
        badge.textContent = cart.item_count;
      } else if (badge) {
        badge.remove();
      }
      cartBtn.classList.toggle('is-filled', cart.item_count > 0);
    }

    // Line items: update qty input + line price, remove rows that hit 0
    const linesByKey = new Map((cart.items || []).map((it) => [it.key, it]));
    document.querySelectorAll('.cart-line').forEach((line) => {
      const key = line.dataset.lineKey;
      const item = linesByKey.get(key);
      if (!item) {
        // Line removed (qty=0 or removed entirely)
        line.remove();
        return;
      }
      const input = line.querySelector('[data-line-qty]');
      if (input) input.value = item.quantity;
      const priceEl = line.querySelector('[data-line-price]');
      if (priceEl) {
        priceEl.textContent = formatMoney(item.final_line_price);
        priceEl.dataset.linePriceCents = item.final_line_price;
      }
    });

    // "(N items)" count label — keep server source-of-truth in sync.
    const countLabel = document.querySelector('[data-cart-count-label]');
    if (countLabel) {
      const noun = cart.item_count === 1 ? (window.__renaT?.itemSingular || 'item')
                                         : (window.__renaT?.itemPlural   || 'items');
      countLabel.textContent = '(' + cart.item_count + ' ' + noun + ')';
    }

    // Free-shipping bar — localized + guards against threshold=0.
    const bar = document.querySelector('[data-free-shipping-bar]');
    if (bar) {
      const threshold = parseInt(bar.dataset.threshold, 10) || 0;
      bar.dataset.subtotal = cart.total_price;
      if (threshold > 0) {
        const pct = Math.min(100, Math.round((cart.total_price * 100) / threshold));
        const fill = bar.querySelector('[data-free-shipping-fill]');
        if (fill) fill.style.width = pct + '%';
        const msg = bar.querySelector('[data-free-shipping-msg]');
        if (msg) {
          if (cart.total_price >= threshold) {
            msg.innerHTML = window.__renaT?.freeShippingReachedHtml
              || '🎉 Your order ships <em>FREE</em>!';
          } else {
            const remaining = threshold - cart.total_price;
            const remainStrong = `<strong>${formatMoney(remaining)}</strong>`;
            const tmpl = window.__renaT?.freeShippingAwayHtml
              || "You're __AMOUNT__ away from <em>free</em> shipping!";
            msg.innerHTML = tmpl.replace('__AMOUNT__', remainStrong);
          }
        }
      }
    }
  };

  // Replace the live drawer's contents from a chunk of HTML that contains
  // a [data-cart-drawer] element (either a Section Rendering API
  // response or a full page fetch).
  const applyDrawerHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('[data-cart-drawer]');
    const currentDrawer = document.querySelector('[data-cart-drawer]');
    if (!newDrawer || !currentDrawer) return false;
    const wasOpen = currentDrawer.getAttribute('aria-hidden') === 'false';
    currentDrawer.innerHTML = newDrawer.innerHTML;
    if (wasOpen) currentDrawer.setAttribute('aria-hidden', 'false');
    return true;
  };

  // Update the cart icon badge from a tiny /cart.js JSON call.
  const refreshCartBadge = async () => {
    try {
      const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
      const cartBtn = document.querySelector('[data-cart-toggle]');
      if (!cartBtn) return;
      let badge = cartBtn.querySelector('.badge');
      if (cart.item_count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'badge';
          cartBtn.appendChild(badge);
        }
        badge.textContent = cart.item_count;
      } else if (badge) {
        badge.remove();
      }
      cartBtn.classList.toggle('is-filled', cart.item_count > 0);
    } catch (e) { /* ignore */ }
  };

  // Mark the drawer as loading so CSS can show a spinner over the items
  // area. Counter-based so concurrent refreshes don't toggle prematurely.
  let _drawerLoadingCount = 0;
  const setDrawerLoading = (on) => {
    const d = document.querySelector('[data-cart-drawer]');
    if (!d) return;
    _drawerLoadingCount = Math.max(0, _drawerLoadingCount + (on ? 1 : -1));
    d.classList.toggle('is-loading', _drawerLoadingCount > 0);
  };

  // Auto-apply / remove the SUBSCRIBE25 discount code based on cart state.
  // Rena's subscription is a line-item property (set by the PDP); Shopify's
  // cart engine can't natively translate that to a discount, so we apply a
  // 25%-off code whenever ALL items in the cart are subscribed, and remove
  // it the moment any line is non-subscribed. Result: the discounted price
  // shows in cart + checkout reliably, without needing Shopify Functions.
  //
  // Trade-off: a mixed cart (1 sub + 1 non-sub) gets NO discount on either,
  // so customers who want the saving keep all lines on subscription. That's
  // an OK behavior — non-sub items in a mostly-sub cart are rare.
  const syncSubscriptionDiscount = async () => {
    try {
      const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
      if (!cart || !cart.items || cart.item_count === 0) return;

      const lineIsSubscribed = (item) => {
        const props = item.properties || {};
        for (const k of Object.keys(props)) {
          const key = String(k).toLowerCase();
          const val = props[k];
          if (val && (key.indexOf('subscribe') >= 0 || key.indexOf('subscription') >= 0 || key.indexOf('frequency') >= 0)) {
            return true;
          }
        }
        return false;
      };

      const allSubscribed = cart.items.every(lineIsSubscribed);
      const discountCodes = cart.discount_codes || [];
      const hasOurCode = discountCodes.some(d => {
        const code = String(d.code || '').toUpperCase();
        return code === 'SUBSCRIBE25' && d.applicable;
      });

      if (allSubscribed && !hasOurCode) {
        // Apply the discount via Shopify's storefront discount URL.
        await fetch('/discount/SUBSCRIBE25', { credentials: 'same-origin' }).catch(() => {});
      } else if (!allSubscribed && hasOurCode) {
        // Remove the code by sending an empty discount via the cart API.
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ discount: '' })
        }).catch(() => {});
      }
    } catch (e) { console.warn('syncSubscriptionDiscount failed', e); }
  };

  // Re-render the cart drawer. Tries Shopify's Section Rendering API
  // first (~100ms when it works), falls back to a current-page fetch
  // (~1-2s) if the section response is empty — which happens for
  // sections rendered from layout/theme.liquid in some Shopify configs.
  const refreshCartDrawer = async () => {
    setDrawerLoading(true);
    // Sync the SUBSCRIBE25 discount BEFORE re-rendering so the drawer
    // reflects the freshly-applied (or removed) discount.
    await syncSubscriptionDiscount();
    try {
      try {
        const res = await fetch('/cart?sections=cart-drawer', { headers: { 'Accept': 'application/json' } });
        if (res.ok) {
          const sections = await res.json().catch(() => ({}));
          const html = sections && sections['cart-drawer'];
          if (html && applyDrawerHtml(html)) {
            await refreshCartBadge();
            return;
          }
        }
      } catch (e) { /* fall through to page fetch */ }

      try {
        const html = await fetch(window.location.pathname, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(r => r.text());
        applyDrawerHtml(html);
        await refreshCartBadge();
      } catch (e) { console.warn('Cart drawer refresh failed', e); }
    } finally {
      setDrawerLoading(false);
    }
  };

  // Optimistic patch — runs immediately on +/-, before the network call.
  // Updates qty input, line price, subtotal, cart badge, and the free-
  // shipping bar from local data attributes (data-line-unit-cents,
  // data-cart-subtotal-cents). The server response then reconciles any
  // divergence (line-level discounts that kick in at qty thresholds,
  // etc.) via patchCartFromJson — but only if no newer optimistic
  // update has happened since the server request was sent (see
  // _lastOptimisticAt below).
  let _lastOptimisticAt = 0;
  const optimisticLineUpdate = (lineKey, newQty) => {
    _lastOptimisticAt = Date.now();
    const line = document.querySelector(`.cart-line[data-line-key="${lineKey}"]`);
    if (!line) return;
    const input = line.querySelector('[data-line-qty]');

    // Derive oldQty from the LAST KNOWN line price ÷ unit price (not from
    // input.value). The direct-edit code path runs `change` AFTER the user
    // already typed the new value into the input, so input.value is already
    // the new qty — reading it would make delta = 0 and starve the badge
    // and count-label updates.
    const unitCents = parseInt(line.dataset.lineUnitCents, 10);
    const priceEl = line.querySelector('[data-line-price]');
    const oldLineCents = priceEl ? (parseInt(priceEl.dataset.linePriceCents, 10) || 0) : 0;
    const oldQty = (isFinite(unitCents) && unitCents > 0)
      ? Math.round(oldLineCents / unitCents)
      : (input ? parseInt(input.value, 10) || 0 : 0);
    if (input) input.value = newQty;

    // Line removed entirely
    if (newQty <= 0) {
      line.style.display = 'none';
    }

    // Line price = unit × qty
    const newLineCents = isFinite(unitCents) ? unitCents * newQty : oldLineCents;
    if (priceEl && isFinite(newLineCents)) {
      priceEl.textContent = formatMoney(newLineCents);
      priceEl.dataset.linePriceCents = newLineCents;
    }

    // Subtotal delta
    const subtotalEl = document.querySelector('[data-cart-subtotal]');
    const oldSubCents = subtotalEl ? (parseInt(subtotalEl.dataset.cartSubtotalCents, 10) || 0) : 0;
    const newSubCents = Math.max(0, oldSubCents + (newLineCents - oldLineCents));
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(newSubCents);
      subtotalEl.dataset.cartSubtotalCents = newSubCents;
    }

    // Badge delta
    const cartBtn = document.querySelector('[data-cart-toggle]');
    let newCount = 0;
    if (cartBtn) {
      let badge = cartBtn.querySelector('.badge');
      const currentCount = badge ? (parseInt(badge.textContent, 10) || 0) : 0;
      newCount = Math.max(0, currentCount + (newQty - oldQty));
      if (newCount > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'badge';
          cartBtn.appendChild(badge);
        }
        badge.textContent = newCount;
      } else if (badge) {
        badge.remove();
      }
      cartBtn.classList.toggle('is-filled', newCount > 0);
    }

    // "(N items)" label inside the drawer's subtotal row — localized.
    const countLabel = document.querySelector('[data-cart-count-label]');
    if (countLabel) {
      const noun = newCount === 1 ? (window.__renaT?.itemSingular || 'item')
                                  : (window.__renaT?.itemPlural   || 'items');
      countLabel.textContent = '(' + newCount + ' ' + noun + ')';
    }

    // If the optimistic update results in an empty cart, hide the
    // subtotal+CTA bars so the drawer doesn't show a "0 items / 0.000 OMR
    // / CHECKOUT" frame while the real empty-state HTML is fetched.
    const topBar = document.querySelector('.cart-drawer__top');
    const ctaBar = document.querySelector('.cart-drawer__cta-bar');
    if (newCount <= 0) {
      if (topBar) topBar.style.display = 'none';
      if (ctaBar) ctaBar.style.display = 'none';
    }

    // Free-shipping bar — localized via window.__renaT, with the formatted
    // money inserted into the translation's {{ amount }} placeholder.
    const bar = document.querySelector('[data-free-shipping-bar]');
    if (bar) {
      const threshold = parseInt(bar.dataset.threshold, 10) || 0;
      bar.dataset.subtotal = newSubCents;
      if (threshold > 0) {
        const pct = Math.min(100, Math.round((newSubCents * 100) / threshold));
        const fill = bar.querySelector('[data-free-shipping-fill]');
        if (fill) fill.style.width = pct + '%';
        const msg = bar.querySelector('[data-free-shipping-msg]');
        if (msg) {
          if (newSubCents >= threshold) {
            msg.innerHTML = window.__renaT?.freeShippingReachedHtml
              || '🎉 Your order ships <em>FREE</em>!';
          } else {
            const remainStrong = `<strong>${formatMoney(threshold - newSubCents)}</strong>`;
            const tmpl = window.__renaT?.freeShippingAwayHtml
              || "You're __AMOUNT__ away from <em>free</em> shipping!";
            msg.innerHTML = tmpl.replace('__AMOUNT__', remainStrong);
          }
        }
      }
    }
  };

  // Per-line request serialization. Shopify's cart API handles
  // concurrent /cart/change.js requests on the same line poorly — three
  // rapid clicks would all fire in parallel and the server would
  // process them out of order, leaving the line at a random qty
  // (sometimes the user's clicks would just be lost entirely). This
  // queues by line key: only one request in flight per line, with the
  // user's latest qty queued up to send next.
  const _qtyState = new Map(); // lineKey → { inFlight: bool, queuedQty: number|null }

  const sendCartChange = async (lineKey, quantity) => {
    const sentAt = Date.now();
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });
      if (!res.ok) return;
      const cart = await res.json();
      if (cart.item_count === 0) {
        await refreshCartDrawer();
        return;
      }
      // Skip the patch if newer optimistic clicks have happened since
      // this request was sent — see comment on _lastOptimisticAt.
      if (_lastOptimisticAt > sentAt) return;
      patchCartFromJson(cart);
    } catch (e) { console.warn('Cart update failed', e); }
  };

  const updateCartLine = (lineKey, quantity) => {
    let s = _qtyState.get(lineKey);
    if (!s) { s = { inFlight: false, queuedQty: null }; _qtyState.set(lineKey, s); }
    s.queuedQty = quantity;
    if (s.inFlight) return;     // already running — drain loop will pick up queuedQty
    s.inFlight = true;
    // Same drawer-wide loading state used by ATC — consistent visual
    // confirmation across all cart mutations.
    setDrawerLoading(true);
    (async () => {
      while (s.queuedQty !== null) {
        const qty = s.queuedQty;
        s.queuedQty = null;
        await sendCartChange(lineKey, qty);
      }
      s.inFlight = false;
      setDrawerLoading(false);
    })();
  };

  document.addEventListener('click', (e) => {
    const dec = e.target.closest('[data-qty-decrease]');
    const inc = e.target.closest('[data-qty-increase]');
    if (!dec && !inc) return;
    const btn = dec || inc;
    const lineKey = btn.dataset.lineKey;
    const input = document.querySelector(`[data-line-qty][data-line-key="${lineKey}"]`);
    if (!input) return;
    let qty = parseInt(input.value, 10) || 1;
    qty = dec ? Math.max(0, qty - 1) : qty + 1;
    // Update the entire visible drawer (qty, line price, subtotal, badge,
    // free-shipping bar) immediately, before the network round-trip.
    // Server response reconciles via patchCartFromJson.
    optimisticLineUpdate(lineKey, qty);
    updateCartLine(lineKey, qty);
  });

  // Direct edit of qty input — mirror the +/- handler's optimistic patch
  // so the line price, subtotal, badge, count label, and shipping bar all
  // update in the same frame as the edit, not after the server round-trip.
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (!input) return;
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    optimisticLineUpdate(input.dataset.lineKey, qty);
    updateCartLine(input.dataset.lineKey, qty);
  });

  cartLink.forEach((el) => el.addEventListener('click', (e) => {
    if (!drawer) return;
    e.preventDefault();
    openDrawer();
  }));
  // Delegated so the close buttons keep working after refreshCartDrawer()
  // swaps the drawer's innerHTML (which destroys the original X and
  // "Continue Shopping" elements that this scope's `cartClose` referenced).
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) closeDrawer();
  });
  if (overlay) overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  /* ------- PDP tabs ------- */
  document.querySelectorAll('[data-tabs]').forEach((tabs) => {
    const triggers = tabs.querySelectorAll('[role="tab"]');
    const panels = tabs.parentElement.querySelectorAll('[role="tabpanel"]');
    triggers.forEach((trigger, i) => {
      trigger.addEventListener('click', () => {
        triggers.forEach((t) => t.setAttribute('aria-selected', 'false'));
        panels.forEach((p) => p.setAttribute('hidden', ''));
        trigger.setAttribute('aria-selected', 'true');
        if (panels[i]) panels[i].removeAttribute('hidden');
      });
    });
  });

  /* ------- PDP gallery thumbs + arrows ------- */
  document.querySelectorAll('[data-pdp-gallery]').forEach((gallery) => {
    const main = gallery.querySelector('[data-pdp-main] img');
    const thumbs = Array.from(gallery.querySelectorAll('[data-pdp-thumb]'));
    const prev = gallery.querySelector('[data-pdp-prev]');
    const next = gallery.querySelector('[data-pdp-next]');
    let activeIdx = thumbs.findIndex((t) => t.classList.contains('is-active'));
    if (activeIdx < 0) activeIdx = 0;
    const activate = (idx) => {
      if (!thumbs.length) return;
      activeIdx = ((idx % thumbs.length) + thumbs.length) % thumbs.length;
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === activeIdx);
        t.setAttribute('aria-pressed', i === activeIdx ? 'true' : 'false');
      });
      const target = thumbs[activeIdx];
      const full = target.dataset.full || target.querySelector('img')?.src;
      if (main && full) main.src = full;
      // Scroll the active thumb into view in the horizontal strip
      target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    };
    // Arrow nav must SKIP variant-filtered thumbs (those with `hidden` set by
    // applyVariantToGallery). Without this guard, prev/next walks straight into
    // hidden slides and the customer sees images that don't belong to the
    // currently-selected variant. We also re-read the active index from the
    // DOM at each click — the variant-change handler updates `is-active` on a
    // different thumb, but `activeIdx` in this closure goes stale; without
    // refreshing, the next arrow click would step from the pre-change index
    // and could either re-enter a hidden slide or jump to the wrong place.
    // Direction = +1 forward, -1 back.
    const stepVisible = (direction) => {
      if (!thumbs.length) return;
      const total = thumbs.length;
      const liveIdx = thumbs.findIndex((t) => t.classList.contains('is-active'));
      const fromIdx = liveIdx >= 0 ? liveIdx : activeIdx;
      for (let step = 1; step <= total; step++) {
        const candidate = ((fromIdx + direction * step) % total + total) % total;
        if (!thumbs[candidate].hidden) { activate(candidate); return; }
      }
    };
    thumbs.forEach((thumb, idx) => thumb.addEventListener('click', () => activate(idx)));
    prev && prev.addEventListener('click', () => stepVisible(-1));
    next && next.addEventListener('click', () => stepVisible(1));
  });

  /* ------- PDP "Pair it with" add-ons -------
     Optimistic UI: the button flips to "Added" + green and the cart drawer
     slides open the INSTANT the customer clicks, before the network round-
     trip completes. /cart/add.js + drawer refresh run in the background
     and patch the drawer once they finish. Feels instant even on slow
     connections; only a real network error rolls back the visual state. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-addon-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId || btn.disabled) return;
    const originalLabel = btn.querySelector('.pdp-addon__add-label');
    const originalText = originalLabel ? originalLabel.textContent : '';
    const addedText = (document.documentElement.lang || '').toLowerCase().startsWith('ar')
      ? 'تمت الإضافة' : 'Added';

    // 1. Instant optimistic feedback — change button, open drawer, AND
    // flip its loading state on NOW (before the network call) so the
    // customer sees the skeleton/loading effect with no lag.
    btn.disabled = true;
    btn.classList.add('is-added');
    if (originalLabel) originalLabel.textContent = addedText;
    openCartDrawer();
    setDrawerLoading(true);

    // 2. Fire the real cart add + drawer refresh in the background.
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
    .then(res => {
      if (!res.ok) throw new Error('add failed');
      return res.json();
    })
    .then(() => {
      // Patch the drawer with the new cart state. refreshCartDrawer
      // manages its own loading toggle, so we pair our manual on with a
      // manual off to keep the counter balanced. Net: skeleton stays on
      // until the drawer HTML has been swapped in.
      if (typeof refreshCartDrawer === 'function') refreshCartDrawer();
      setDrawerLoading(false);
      // Revert label after a beat so the customer can re-add if they want.
      setTimeout(() => {
        btn.classList.remove('is-added');
        if (originalLabel) originalLabel.textContent = originalText;
        btn.disabled = false;
      }, 1800);
    })
    .catch((err) => {
      // Roll back the optimistic UI on a real failure.
      console.warn('Add-on add failed', err);
      setDrawerLoading(false);
      btn.classList.remove('is-added');
      if (originalLabel) originalLabel.textContent = originalText;
      btn.disabled = false;
    });
  });

  /* ------- PDP purchase toggle ------- */
  document.querySelectorAll('[data-purchase-toggle]').forEach((toggle) => {
    const opts = toggle.querySelectorAll('[role="radio"]');
    opts.forEach((opt) => {
      opt.addEventListener('click', () => {
        opts.forEach((o) => o.setAttribute('aria-checked', 'false'));
        opt.setAttribute('aria-checked', 'true');
        const input = opt.querySelector('input[type="radio"]');
        if (input) input.checked = true;
      });
    });
  });

  /* ------- Free shipping progress bar ------- */
  function updateShippingBar() {
    const bar = document.querySelector('[data-free-shipping-bar]');
    if (!bar) return;
    const threshold = parseInt(bar.dataset.threshold, 10) || 25000;
    const subtotal = parseInt(bar.dataset.subtotal, 10) || 0;
    const pct = Math.min(100, (subtotal / threshold) * 100);
    const fill = bar.querySelector('.free-shipping-bar__fill');
    if (fill) fill.style.width = pct + '%';
  }
  updateShippingBar();

  /* ------- Sticky header scroll state ------- */
  const header = document.querySelector('.site-header');
  if (header) {
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ------- Mobile nav ------- */
  const menuToggle = document.querySelector('[data-mobile-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener('click', () => {
      const open = mobileMenu.getAttribute('aria-hidden') === 'false';
      mobileMenu.setAttribute('aria-hidden', open ? 'true' : 'false');
    });
  }

  /* ------- PDP hero buy-box ------- */
  document.querySelectorAll('[data-pdp]').forEach((pdp) => {
    const variantsTag = pdp.querySelector('[data-pdp-variants]');
    let variants = [];
    try { variants = variantsTag ? JSON.parse(variantsTag.textContent) : []; } catch {}

    const mainImg   = pdp.querySelector('[data-pdp-main-img]') || pdp.querySelector('.pdp-gallery__media-img');
    const thumbs    = pdp.querySelectorAll('[data-pdp-thumb]');
    const scentBtns = pdp.querySelectorAll('.pdp-scent-pill');
    const sizeBtns  = pdp.querySelectorAll('.pdp-size-btn');
    const subCheck  = pdp.querySelector('[data-pdp-sub-check]');
    const priceEl   = pdp.querySelector('[data-pdp-price]');
    const strikeEl  = pdp.querySelector('[data-pdp-strike]');
    const atcPriceEl = pdp.querySelector('[data-pdp-atc-price]');
    const atcStrikeEl = pdp.querySelector('[data-pdp-atc-strike]');
    const variantIdInput = pdp.querySelector('[data-pdp-variant-id]');
    const tabBtns   = pdp.querySelectorAll('.pdp-tab');
    const tabPanels = pdp.querySelectorAll('.pdp-tabs__panel');

    // Gallery: click a thumb → swap main image. We pull the high-res URL
    // off `data-full-src`/`data-full-srcset` (emitted by Liquid at render time)
    // so the main slot stays sharp on retina — copying the thumbnail's own
    // tiny src would upscale a 240px image into the 800–1200px main slot.
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.toggle('is-active', t === thumb));
        if (!mainImg) return;
        const fullSrc = thumb.getAttribute('data-full-src');
        const fullSrcset = thumb.getAttribute('data-full-srcset');
        if (fullSrcset) {
          mainImg.setAttribute('srcset', fullSrcset);
        } else {
          mainImg.removeAttribute('srcset');
        }
        if (fullSrc) {
          mainImg.src = fullSrc;
        } else {
          const img = thumb.querySelector('img');
          if (img) mainImg.src = img.currentSrc || img.src;
        }
      });
    });

    // Scent + size + sub state → find matching variant, update UI
    const refresh = () => {
      if (!variants.length) return;
      const scent = pdp.querySelector('.pdp-scent-pill[aria-pressed="true"]');
      const size  = pdp.querySelector('.pdp-size-btn[aria-pressed="true"]');
      const scentVal = scent ? scent.dataset.scentValue : null;
      const sizeVal  = size ? size.dataset.sizeValue : null;
      const isSub = subCheck ? subCheck.checked : false;

      const match = variants.find(v =>
        (!scentVal || !v.scent || v.scent === scentVal) &&
        (!sizeVal  || !v.size  || v.size  === sizeVal)
      ) || variants[0];
      if (!match) return;

      const priceHtml = fixPrice(isSub ? match.price_subscription_html : match.price_html);
      if (priceEl) priceEl.textContent = priceHtml;
      if (atcPriceEl) atcPriceEl.textContent = priceHtml;

      // Unified strike-through baseline. Priority order:
      //   1. Pack tier list price          → smallest pack × multiplier (max stacked baseline)
      //   2. Sub on + Shopify compare-at   → compare_at_html (full retail vs sub price)
      //   3. Sub on (no compare-at)        → price_html (variant regular vs sub price)
      //   4. Compare-at only               → compare_at_html (sale, no sub)
      //   5. Otherwise                     → null
      // The first match wins, so the customer always sees the highest
      // applicable baseline.
      const pctNow = match.savings_pct || 0;
      let strikeSrc = null;
      if (pctNow > 0 && match.list_price_html) {
        strikeSrc = match.list_price_html;
      } else if (isSub && match.compare_at_html) {
        strikeSrc = match.compare_at_html;
      } else if (isSub) {
        strikeSrc = match.price_html;
      } else if (match.compare_at_html) {
        strikeSrc = match.compare_at_html;
      }
      const compareHtml = strikeSrc ? fixPrice(strikeSrc) : null;
      // Hide the legacy server-rendered `.pdp-price__list` element (kept
      // for ATC mirror compat) — the new `[data-pdp-list-price]` block
      // is the one customers see.
      if (strikeEl && strikeEl.parentElement) {
        strikeEl.parentElement.hidden = true;
        if (compareHtml) strikeEl.textContent = compareHtml;
      }
      if (atcStrikeEl) {
        if (compareHtml) { atcStrikeEl.textContent = compareHtml; atcStrikeEl.hidden = false; }
        else { atcStrikeEl.hidden = true; }
      }

      if (variantIdInput) variantIdInput.value = match.id;

      // "List price: $X" strike + "X% OFF for getting a larger pack" banner.
      // Both derive from the per-ml savings vs the smallest pack. Hidden on
      // the base size, shown on any larger pack.
      const savingsBanner = pdp.querySelector('[data-pdp-savings-banner]');
      const savingsPctEl  = pdp.querySelector('[data-pdp-savings-pct]');
      const listPriceWrap = pdp.querySelector('[data-pdp-list-price]');
      const listPriceVal  = pdp.querySelector('[data-pdp-list-price-value]');
      const pct = match.savings_pct || 0;
      if (savingsBanner) {
        if (pct > 0) {
          if (savingsPctEl) savingsPctEl.textContent = pct + '% ';
          savingsBanner.setAttribute('data-active', 'true');
        } else {
          savingsBanner.setAttribute('data-active', 'false');
        }
      }
      if (listPriceWrap) {
        // Mirror the unified strike priority for the user-visible list-price
        // line (same source as `strikeSrc` above):
        //   - Pack tier               → smallest pack × multiplier (max stacked baseline)
        //   - Sub + Shopify compare-at→ compare_at_html (bundles + subscription)
        //   - Sub only                → variant's regular (non-sub) price
        //   - Compare-at only         → compare_at_html (sale, no sub)
        if (compareHtml) {
          if (listPriceVal) listPriceVal.textContent = compareHtml;
          listPriceWrap.setAttribute('data-active', 'true');
        } else {
          listPriceWrap.setAttribute('data-active', 'false');
        }
      }

      // Filter the gallery to show thumbs that are either shared (no owner)
      // OR owned by the current variant. Two distinct ownership attrs:
      //   data-variant-ids       — combined owners (featured + alt-text).
      //                            Drives visibility.
      //   data-pin-variant-ids   — featured-image owners only. Drives the
      //                            "pin to top of strip" behavior, so
      //                            alt-text size-restricted images stay in
      //                            their natural admin gallery position.
      const currentVariantId = String(match.id);
      thumbs.forEach((thumb) => {
        const owners = thumb.getAttribute('data-variant-ids');
        const pinOwners = thumb.getAttribute('data-pin-variant-ids');
        const li = thumb.closest('li');
        // Visibility
        let isVisible;
        if (!owners) {
          isVisible = true;
        } else {
          isVisible = owners.split(',').includes(currentVariantId);
        }
        if (li) li.hidden = !isVisible;
        thumb.hidden = !isVisible;
        // Pin-to-top: only when this thumb is the variant's FEATURED image.
        const isPinned = pinOwners && pinOwners.split(',').includes(currentVariantId);
        if (li) li.style.order = isPinned ? '-1' : '0';
        // If the now-hidden thumb was active, drop its active state.
        if (!isVisible && thumb.classList.contains('is-active')) {
          thumb.classList.remove('is-active');
        }
      });
      // Make sure the variant's own thumb is marked active (if it exists).
      const ownThumb = Array.from(thumbs).find((t) => {
        const owners = t.getAttribute('data-variant-ids');
        return owners && owners.split(',').includes(currentVariantId);
      });
      if (ownThumb) {
        thumbs.forEach((t) => t.classList.toggle('is-active', t === ownThumb));
      }

      if (mainImg && match.image_url && mainImg.src !== match.image_url) {
        mainImg.src = match.image_url;
        mainImg.removeAttribute('srcset');
      }
    };

    scentBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        scentBtns.forEach(b => {
          const sel = b === btn;
          b.classList.toggle('is-active', sel);
          b.setAttribute('aria-pressed', sel ? 'true' : 'false');
        });
        refresh();
      });
    });
    sizeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        sizeBtns.forEach(b => {
          const sel = b === btn;
          b.classList.toggle('is-active', sel);
          b.setAttribute('aria-pressed', sel ? 'true' : 'false');
        });
        refresh();
      });
    });
    if (subCheck) {
      const subBox = subCheck.closest('.pdp-sub-box');
      const reflectChecked = () => {
        if (subBox) subBox.classList.toggle('is-checked', subCheck.checked);
      };
      reflectChecked();
      subCheck.addEventListener('change', () => { reflectChecked(); refresh(); syncSubFormProps(); });
    }

    // Sync the subscribe + frequency UI to the hidden form properties so
    // the order line item carries the customer's selection. Future
    // subscription-app integration reads these properties to set up the
    // recurring schedule. Empty value = property hidden from cart display.
    const subFreqSel  = pdp.querySelector('[data-pdp-sub-freq]');
    const subPropEl   = pdp.querySelector('[data-pdp-sub-prop]');
    const freqPropEl  = pdp.querySelector('[data-pdp-freq-prop]');
    const syncSubFormProps = () => {
      const isSub = subCheck ? subCheck.checked : false;
      if (subPropEl)  subPropEl.value  = isSub ? 'Yes (25% off)' : '';
      if (freqPropEl) freqPropEl.value = isSub && subFreqSel ? subFreqSel.value : '';
    };
    if (subFreqSel) subFreqSel.addEventListener('change', syncSubFormProps);
    syncSubFormProps();

    // Tabs — animate the panels-wrapper height so the page below glides
    // smoothly when switching between tabs of different content lengths.
    const panelsWrap = pdp.querySelector('[data-pdp-panels]');
    tabBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.pdpTab;
        tabBtns.forEach(b => {
          const sel = b === btn;
          b.classList.toggle('is-active', sel);
          b.setAttribute('aria-selected', sel ? 'true' : 'false');
        });

        if (!panelsWrap) {
          tabPanels.forEach(p => p.hidden = p.dataset.pdpPanel !== key);
          return;
        }

        // Capture current height, swap visibility, measure target height,
        // then transition from old → new on the wrapper. After the transition
        // finishes we unset the inline height so the panel can grow naturally
        // if its content (e.g. expanded ingredients list) changes later.
        const fromH = panelsWrap.offsetHeight;
        tabPanels.forEach(p => p.hidden = p.dataset.pdpPanel !== key);
        panelsWrap.style.height = 'auto';
        const toH = panelsWrap.offsetHeight;
        panelsWrap.style.height = fromH + 'px';
        // rAF guarantees the browser commits the start height in the next
        // paint frame before we set the target — more reliable than a
        // forced reflow read for kicking off the transition.
        requestAnimationFrame(() => {
          panelsWrap.style.height = toH + 'px';
        });
        const cleanup = (e) => {
          if (e && e.propertyName !== 'height') return;
          panelsWrap.style.height = '';
          panelsWrap.removeEventListener('transitionend', cleanup);
        };
        panelsWrap.addEventListener('transitionend', cleanup);
      });
    });

    refresh();

    /* Sticky buy bar — show when the main buy-box leaves the viewport. */
    const stickyBar = document.querySelector('[data-pdp-sticky]');
    const buyBox    = pdp.querySelector('[data-pdp-buy-box]');
    if (stickyBar && buyBox && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const isOut = !entry.isIntersecting;
          stickyBar.setAttribute('aria-hidden', isOut ? 'false' : 'true');
        });
      }, { rootMargin: '-200px 0px 0px 0px' });
      io.observe(buyBox);
    }

    // Sticky bar mirrors the same variant state
    const syncSticky = () => {
      if (!stickyBar) return;
      const scent = pdp.querySelector('.pdp-scent-pill[aria-pressed="true"]');
      const size  = pdp.querySelector('.pdp-size-btn[aria-pressed="true"]');
      const isSub = subCheck ? subCheck.checked : false;
      const scentEl = stickyBar.querySelector('[data-pdp-sticky-scent]');
      const sizeEl  = stickyBar.querySelector('[data-pdp-sticky-size]');
      const planEl  = stickyBar.querySelector('[data-pdp-sticky-plan]');
      const priceEl = stickyBar.querySelector('[data-pdp-sticky-price]');
      const mobilePriceEl = stickyBar.querySelector('[data-pdp-sticky-mobile-price]');
      const strikeEl= stickyBar.querySelector('[data-pdp-sticky-strike]');
      if (scentEl && scent) scentEl.textContent = scent.getAttribute('aria-label');
      if (sizeEl  && size)  sizeEl.textContent  = size.querySelector('.pdp-size-name')?.textContent || '';
      if (planEl) {
        const subLabel = planEl.getAttribute('data-label-subscription') || 'Subscription';
        const oneLabel = planEl.getAttribute('data-label-one-time') || 'One-time';
        planEl.textContent = isSub ? subLabel : oneLabel;
      }
      const liveAtcPrice = pdp.querySelector('[data-pdp-atc-price]');
      const liveAtcStrike = pdp.querySelector('[data-pdp-atc-strike]');
      const mobileStrikeEl = stickyBar.querySelector('[data-pdp-sticky-mobile-strike]');
      if (priceEl && liveAtcPrice) priceEl.textContent = liveAtcPrice.textContent;
      if (mobilePriceEl && liveAtcPrice) mobilePriceEl.textContent = liveAtcPrice.textContent;
      if (strikeEl && liveAtcStrike) {
        strikeEl.textContent = liveAtcStrike.textContent;
        strikeEl.hidden = liveAtcStrike.hidden;
      }
      if (mobileStrikeEl && liveAtcStrike) {
        mobileStrikeEl.textContent = liveAtcStrike.textContent;
        mobileStrikeEl.hidden = liveAtcStrike.hidden;
      }
    };
    pdp.addEventListener('click', syncSticky);
    if (subCheck) subCheck.addEventListener('change', syncSticky);
    syncSticky();

    // Sticky bar ATC — submits the main form. In the dropps-style design
    // the entire bar IS the button (the [data-pdp-sticky-atc] attribute
    // sits on the bar element itself); fall back to a child for older markup.
    const stickyAtc = stickyBar && (
      stickyBar.matches('[data-pdp-sticky-atc]')
        ? stickyBar
        : stickyBar.querySelector('[data-pdp-sticky-atc]')
    );
    if (stickyAtc) {
      stickyAtc.addEventListener('click', () => {
        // Click the real ATC button (inside the form) so the delegated
        // AJAX cart handler fires and opens the drawer. requestSubmit()
        // would fire a `submit` event the AJAX handler doesn't listen for,
        // causing a native page navigation to /cart.
        const atcBtn = pdp.querySelector('[data-pdp-atc]');
        if (atcBtn) atcBtn.click();
      });
    }
  });

  /* ------- "Review this Product" star picker -------
     Hover lights up stars 1..N; click locks them in. */
  document.querySelectorAll('[data-pdp-reviews] .pdp-reviews__rate').forEach((group) => {
    const stars = Array.from(group.querySelectorAll('.pdp-reviews__rate-star'));
    if (!stars.length) return;

    const paint = (n, cls) => {
      stars.forEach((s, idx) => s.classList.toggle(cls, idx < n));
    };
    stars.forEach((star, idx) => {
      star.addEventListener('mouseenter', () => paint(idx + 1, 'is-filled'));
      star.addEventListener('focus',      () => paint(idx + 1, 'is-filled'));
      star.addEventListener('click', () => {
        paint(idx + 1, 'is-active');
        paint(0, 'is-filled');
      });
    });
    group.addEventListener('mouseleave', () => paint(0, 'is-filled'));
  });

  /* ------- "Made Clean. Made To Clean." carousel dots (mobile only) -------
     On mobile, the 3 drop-cards become a horizontal scroll-snap carousel.
     Wire each dot to scrollIntoView the matching card, and use an
     IntersectionObserver scoped to the scroller to keep the active dot in
     sync as the user swipes. On desktop the dots are hidden via CSS and the
     observer is a harmless no-op (cards are always all visible). */
  document.querySelectorAll('[data-pdp-clean-drops]').forEach((scroller) => {
    const dotsList = scroller.parentElement && scroller.parentElement.querySelector('[data-pdp-clean-dots]');
    if (!dotsList) return;
    const dots  = Array.from(dotsList.querySelectorAll('.pdp-clean__dot'));
    const cards = Array.from(scroller.children);
    if (!dots.length || !cards.length) return;

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const target = cards[i];
        if (!target) return;
        // Explicit scrollTo on the scroller itself — more reliable than
        // scrollIntoView, which sometimes scrolls the page instead of the
        // local scroll container.
        scroller.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      });
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        // Pick the entry with the highest intersection ratio — that's the
        // card most-in-view, which becomes the active dot.
        let best = null;
        entries.forEach((entry) => {
          if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
        });
        if (!best || !best.isIntersecting) return;
        const i = cards.indexOf(best.target);
        dots.forEach((d, j) => d.classList.toggle('is-active', j === i));
      }, { root: scroller, threshold: [0.55, 0.75, 0.95] });
      cards.forEach((c) => io.observe(c));
    }
  });

  /* ------- Quick Add modal (product cards) -------
     Click on a card's "Quick add +" link opens that card's existing
     .product-card__buy-box as a centered modal over a backdrop. Closes via
     X click, backdrop click, or Esc. Card scroll is locked while open.
     The buy-box's variant pickers / ATC form are already wired by their
     own handlers — we just visually promote them. */
  const quickAddBackdrop = document.querySelector('[data-quick-add-backdrop]');
  const closeQuickAdd = () => {
    document.querySelectorAll('.product-card.is-quick-add-open').forEach((c) => {
      c.classList.remove('is-quick-add-open');
    });
    if (quickAddBackdrop) quickAddBackdrop.classList.remove('is-open');
    document.body.style.overflow = '';
  };
  document.querySelectorAll('[data-quick-add]').forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const card = trigger.closest('.product-card');
      if (!card) return;
      closeQuickAdd();
      card.classList.add('is-quick-add-open');
      if (quickAddBackdrop) quickAddBackdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });
  });
  if (quickAddBackdrop) quickAddBackdrop.addEventListener('click', closeQuickAdd);
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-quick-close]')) closeQuickAdd();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeQuickAdd();
  });

  /* ------- Customer Images & Videos arrows (in pdp-reviews-list) -------
     Scroll the track by ~80% of its visible width on prev/next clicks.
     Arrows are hidden on small screens via CSS — swipe handles those. */
  document.querySelectorAll('[data-pdp-reviews-media]').forEach((wrap) => {
    const track = wrap.querySelector('[data-media-track]');
    const prev  = wrap.querySelector('[data-media-prev]');
    const next  = wrap.querySelector('[data-media-next]');
    if (!track) return;
    const step = () => Math.round(track.clientWidth * 0.8);
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left:  step(), behavior: 'smooth' }));
  });

  /* ------- Ingredients drawer ------- */
  const ingDrawer  = document.querySelector('[data-ing-drawer]');
  const ingOverlay = document.querySelector('[data-ing-overlay]');
  if (ingDrawer) {
    const openIng = () => {
      ingDrawer.setAttribute('aria-hidden', 'false');
      if (ingOverlay) ingOverlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };
    const closeIng = () => {
      ingDrawer.setAttribute('aria-hidden', 'true');
      if (ingOverlay) ingOverlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };
    document.querySelectorAll('[data-ing-toggle]').forEach((el) => {
      el.addEventListener('click', (e) => { e.preventDefault(); openIng(); });
    });
    document.querySelectorAll('[data-ing-close]').forEach((el) => el.addEventListener('click', closeIng));
    if (ingOverlay) ingOverlay.addEventListener('click', closeIng);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && ingDrawer.getAttribute('aria-hidden') === 'false') closeIng();
    });
  }

  /* ------- Product card buy-box (scent / size / plan) -------
     Reads the [data-product-variants] JSON inside each card and updates
     price + size meta + ATC variant ID when scent/size/plan changes. */
  const getCardVariants = (card) => {
    if (!card) return [];
    if (card._variantsCache) return card._variantsCache;
    const tag = card.querySelector('[data-product-variants]');
    if (!tag) return [];
    try {
      card._variantsCache = JSON.parse(tag.textContent);
      return card._variantsCache;
    } catch { return []; }
  };

  const refreshCard = (card) => {
    if (!card) return;
    const variants = getCardVariants(card);
    if (!variants.length) return;
    const scentBtn = card.querySelector('.variant-icon-btn[aria-pressed="true"]');
    const sizeBtn  = card.querySelector('.size-pill[aria-pressed="true"]');
    const planBtn  = card.querySelector('.plan-btn[aria-pressed="true"]');
    const scent = scentBtn ? scentBtn.dataset.scentValue : (variants[0] && variants[0].scent);
    const size  = sizeBtn  ? sizeBtn.dataset.sizeValue   : (variants[0] && variants[0].size);
    const isSubscription = planBtn && planBtn.dataset.plan === 'subscription';

    const match = variants.find(v =>
      (!scent || !v.scent || v.scent === scent) &&
      (!size  || !v.size  || v.size  === size)
    ) || variants[0];
    if (!match) return;

    const priceHtml = fixPrice(isSubscription ? match.price_subscription_html : match.price_html);
    const priceEl = card.querySelector('.product-card__price');
    if (priceEl) priceEl.textContent = priceHtml;
    const atcPriceEl = card.querySelector('[data-card-price]');
    if (atcPriceEl) atcPriceEl.textContent = priceHtml;
    const countEl = card.querySelector('.product-card__count');
    if (countEl && match.size_label) countEl.textContent = match.size_label;
    const variantInput = card.querySelector('[data-variant-id]');
    if (variantInput) variantInput.value = match.id;
    const captionEl = card.querySelector('.product-card__caption');
    if (captionEl) {
      const subText = captionEl.getAttribute('data-caption-subscription') || captionEl.textContent;
      const oneText = captionEl.getAttribute('data-caption-one-time') || captionEl.textContent;
      captionEl.textContent = isSubscription ? subText : oneText;
    }
    // Swap the card image to the variant's own image when available
    const mediaImg = card.querySelector('.product-card__media img');
    if (mediaImg && match.image_url && mediaImg.src !== match.image_url) {
      mediaImg.src = match.image_url;
      mediaImg.removeAttribute('srcset'); // prevent the responsive set from overriding
    }
  };

  document.addEventListener('click', (e) => {
    // "Change scent" toggle — reveal/hide the scent swatches
    const scentToggle = e.target.closest('[data-scent-toggle]');
    if (scentToggle) {
      e.preventDefault();
      const card = scentToggle.closest('.product-card');
      const icons = card && card.querySelector('.variant-icons');
      if (icons) icons.hidden = !icons.hidden;
      return;
    }

    // Scent swatch click — update selected state + badge + price
    const scentBtn = e.target.closest('.variant-icon-btn');
    if (scentBtn) {
      const card = scentBtn.closest('.product-card');
      card.querySelectorAll('.variant-icon-btn').forEach(b => b.setAttribute('aria-pressed', 'false'));
      scentBtn.setAttribute('aria-pressed', 'true');
      const badge = card.querySelector('.variant-badge--scent');
      if (badge) {
        badge.textContent = scentBtn.getAttribute('aria-label');
        badge.style.background = scentBtn.style.background;
        badge.setAttribute('data-scent', scentBtn.getAttribute('data-scent-value'));
      }
      refreshCard(card);
      return;
    }

    // Size pill — radio behavior + refresh
    const sizePill = e.target.closest('.size-pill');
    if (sizePill) {
      const card = sizePill.closest('.product-card');
      const group = sizePill.closest('.product-card__sizes');
      if (group) group.querySelectorAll('.size-pill').forEach(b => b.setAttribute('aria-pressed', b === sizePill ? 'true' : 'false'));
      refreshCard(card);
      return;
    }

    // Plan (subscription / one-time) — radio behavior + refresh
    const planBtn = e.target.closest('.plan-btn');
    if (planBtn) {
      const card = planBtn.closest('.product-card');
      const group = planBtn.closest('.product-card__plan');
      if (group) group.querySelectorAll('.plan-btn').forEach(b => b.setAttribute('aria-pressed', b === planBtn ? 'true' : 'false'));
      refreshCard(card);
      return;
    }
  });

  /* ------- Hero slideshow ------- */
  document.querySelectorAll('[data-hero-slideshow]').forEach((show) => {
    const slides = show.querySelectorAll('[data-hero-slide]');
    const dots   = show.querySelectorAll('[data-hero-dot]');
    if (slides.length < 2) return;
    let i = 0;
    const go = (n) => {
      i = (n + slides.length) % slides.length;
      slides.forEach((s, idx) => s.hidden = idx !== i);
      dots.forEach((d, idx) => d.setAttribute('aria-pressed', idx === i ? 'true' : 'false'));
    };
    dots.forEach((d, idx) => d.addEventListener('click', () => { go(idx); restart(); }));
    const interval = parseInt(show.dataset.heroAutoplay, 10);
    let timer = null;
    const restart = () => { if (timer) clearInterval(timer); if (interval > 0) timer = setInterval(() => go(i + 1), interval); };
    restart();
    show.addEventListener('mouseenter', () => timer && clearInterval(timer));
    show.addEventListener('mouseleave', restart);

    /* Pointer swipe (touch + mouse drag) — mobile only. */
    const isMobileViewport = () => window.matchMedia('(max-width: 900px)').matches;
    let startX = 0;
    let startY = 0;
    let pointerId = null;
    const THRESHOLD = 40;
    show.addEventListener('pointerdown', (e) => {
      if (!isMobileViewport()) return;
      if (e.target.closest('[data-hero-dot]')) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
    });
    document.addEventListener('pointerup', (e) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      pointerId = null;
      if (!isMobileViewport()) return;
      if (Math.abs(dx) > THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        go(i + (dx < 0 ? 1 : -1));
        restart();
      }
    });
    document.addEventListener('pointercancel', () => { pointerId = null; });

    /* Trackpad horizontal wheel — accumulates deltaX across wheel
       events within a single gesture so even slow/gentle swipes can
       trigger a slide change. After one slide advances we lock
       further changes until the wheel idles for 150ms (gesture end),
       so one swipe = exactly one slide. */
    let wheelAccum = 0;
    let wheelLocked = false;
    let wheelIdleTimer = null;
    const WHEEL_THRESHOLD = 30;
    show.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      clearTimeout(wheelIdleTimer);
      wheelIdleTimer = setTimeout(() => { wheelLocked = false; wheelAccum = 0; }, 150);
      if (wheelLocked) return;
      wheelAccum += e.deltaX;
      if (Math.abs(wheelAccum) < WHEEL_THRESHOLD) return;
      wheelLocked = true;
      go(i + (wheelAccum > 0 ? 1 : -1));
      restart();
    }, { passive: false });
  });

  /* ------- Reviews slider ------- */
  document.querySelectorAll('[data-reviews-slider]').forEach((slider) => {
    const track  = slider.querySelector('[data-rev-track]');
    const slides = slider.querySelectorAll('[data-rev-slide]');
    const dots   = slider.querySelectorAll('[data-rev-dot]');
    const prev   = slider.querySelector('[data-rev-prev]');
    const next   = slider.querySelector('[data-rev-next]');
    if (!slides.length || !track) return;
    let i = 0;
    // RTL flips the inline axis — translateX(-100%) shows slide 1 in
    // LTR, but in RTL the track's right edge is the start, so we need
    // a positive translateX to advance.
    const isRTL = () => document.documentElement.dir === 'rtl';
    const show = (n) => {
      i = (n + slides.length) % slides.length;
      const offset = (isRTL() ? 1 : -1) * i * 100;
      track.style.transform = `translateX(${offset}%)`;
      slides.forEach((s, idx) => s.setAttribute('aria-hidden', idx === i ? 'false' : 'true'));
      dots.forEach((d, idx) => d.setAttribute('aria-pressed', idx === i ? 'true' : 'false'));
    };
    // Initial position
    show(0);
    prev && prev.addEventListener('click', () => show(i - 1));
    next && next.addEventListener('click', () => show(i + 1));
    dots.forEach((d, idx) => d.addEventListener('click', () => show(idx)));

    /* Pointer swipe (touch + mouse drag) — mobile only. */
    const isMobileViewport = () => window.matchMedia('(max-width: 900px)').matches;
    let startX = 0;
    let startY = 0;
    let pointerId = null;
    const THRESHOLD = 40;
    slider.addEventListener('pointerdown', (e) => {
      if (!isMobileViewport()) return;
      if (e.target.closest('[data-rev-dot]') || e.target.closest('[data-rev-prev]') || e.target.closest('[data-rev-next]')) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
    });
    document.addEventListener('pointerup', (e) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      pointerId = null;
      if (!isMobileViewport()) return;
      if (Math.abs(dx) > THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        // In RTL the next slide visually sits on the LEFT, so the natural
        // gesture flips: swipe right → next, swipe left → previous.
        const baseDir = dx < 0 ? 1 : -1;
        show(i + (isRTL() ? -baseDir : baseDir));
      }
    });
    document.addEventListener('pointercancel', () => { pointerId = null; });

    /* Desktop: no wheel/trackpad scrolling — users navigate with the
       prev/next arrows. Mobile touch swipe is handled above by pointerdown/
       pointerup. Keeping the slider intentionally non-scrollable on desktop
       avoids accidental advances and any momentum-related quirks. */
  });

  /* ------- Announcement bar ------- */
  document.querySelectorAll('[data-announcement-bar]').forEach((bar) => {
    const slides = bar.querySelectorAll('[data-ann-slide]');
    const prev   = bar.querySelector('[data-ann-prev]');
    const next   = bar.querySelector('[data-ann-next]');
    if (slides.length < 2) return;
    let i = 0;
    const show = (n) => {
      i = (n + slides.length) % slides.length;
      slides.forEach((s, idx) => s.hidden = idx !== i);
    };
    prev && prev.addEventListener('click', () => show(i - 1));
    next && next.addEventListener('click', () => show(i + 1));
  });

  /* ------- Footer accordion (event delegation — robust against late DOM) ------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-footer-accordion]');
    if (!btn) return;
    e.preventDefault();
    const col = btn.closest('.site-footer__col');
    if (!col) return;
    const open = col.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* ------- FAQ accordions (pre-footer and PDP) ------- */
  document.querySelectorAll('[data-prefoot-faq], [data-pdp-faq]').forEach((faq) => {
    faq.querySelectorAll('.prefoot-faq__q, .pdp-faq__q').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.prefoot-faq__item, .pdp-faq__item');
        if (!item) return;
        const open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  });
})();
